import { useCallback, useEffect, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { getAuditRecord } from "./audit.gateway";
import type { AuditDetailState } from "./audit.types";

export const AUDIT_DETAIL_SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Entre novamente para consultar os detalhes da auditoria.";
export const AUDIT_DETAIL_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar este registro de auditoria.";
export const AUDIT_DETAIL_NOT_FOUND_MESSAGE = "Registro de auditoria não encontrado.";
export const AUDIT_DETAIL_ERROR_MESSAGE = "Não foi possível carregar os detalhes da auditoria.";

const INITIAL_STATE: AuditDetailState = {
  status: "loading",
  record: null,
  errorMessage: null,
};

const NOT_FOUND_STATE: AuditDetailState = {
  status: "notFound",
  record: null,
  errorMessage: AUDIT_DETAIL_NOT_FOUND_MESSAGE,
};

interface UseAuditRecordDetailResult extends AuditDetailState {
  reload: () => void;
}

/**
 * Cada falha tem um estado próprio; nenhuma delas devolve dados do registro,
 * para que uma carga incompleta não seja lida como o evento consultado.
 */
function toFailure(error: unknown): AuditDetailState {
  const status = isAPIError(error) ? error.status : null;

  // 401 chega aqui só quando a reautenticação global do HttpClient não corre;
  // ainda assim a sessão expirada é anunciada como tal, e não como falha de carga.
  if (status === 401) {
    return {
      status: "sessionExpired",
      record: null,
      errorMessage: AUDIT_DETAIL_SESSION_EXPIRED_MESSAGE,
    };
  }

  if (status === 403) {
    return { status: "forbidden", record: null, errorMessage: AUDIT_DETAIL_FORBIDDEN_MESSAGE };
  }

  if (status === 404) return NOT_FOUND_STATE;

  return {
    status: "error",
    record: null,
    errorMessage: getErrorMessage(error) || AUDIT_DETAIL_ERROR_MESSAGE,
  };
}

/** Consulta o detalhe do registro pedido; nenhuma escrita é disparada (RN005). */
export function useAuditRecordDetail(id: string): UseAuditRecordDetailResult {
  const [state, setState] = useState<AuditDetailState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Fechar o modal ou trocar de registro descarta a resposta em trânsito.
    const controller = new AbortController();

    setState(INITIAL_STATE);

    getAuditRecord({ id, signal: controller.signal })
      .then((record) => {
        if (cancelled) return;
        setState(record ? { status: "success", record, errorMessage: null } : NOT_FOUND_STATE);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState(toFailure(error));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
